import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export const usePropertySharing = (propertyId) => {
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchSharingInfo = useCallback(async () => {
        if (!propertyId || !user) return;
        setLoading(true);

        try {
            // 1. Fetch Members
            // We need to join with profiles to get names/emails, but profiles table might not have email directly visible?
            // Usually auth.users email is private. Profiles has full_name.
            // We might need to store email in invites at least.
            // For members, we can fetch their profile info.
            const { data: memberData, error: memberError } = await supabase
                .from("property_members")
                .select(`
                    id,
                    user_id,
                    role,
                    created_at,
                    profiles:user_id ( full_name ) 
                `)
                .eq("property_id", propertyId);

            if (memberError) throw memberError;

            // 2. Fetch Invites
            const { data: inviteData, error: inviteError } = await supabase
                .from("property_invites")
                .select("*")
                .eq("property_id", propertyId)
                .eq("status", "pending");

            if (inviteError) throw inviteError;

            setMembers(memberData || []);
            setInvites(inviteData || []);

        } catch (error) {
            console.error("Error fetching sharing info:", error);
            // Don't toast here to avoid spam on load, just log
        } finally {
            setLoading(false);
        }
    }, [propertyId, user]);

    const inviteUser = async (email) => {
        if (!propertyId) return false;

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address");
            return false;
        }

        try {
            // Check if already member (optimistic check, real check in DB constraint/logic if complex)
            // But we don't have emails of members easily unless we store them. 
            // We'll proceed to create invite.

            // Check if invite already exists
            const existingInvite = invites.find(i => i.invitee_email.toLowerCase() === email.toLowerCase());
            if (existingInvite) {
                toast.error("Invite already sent to this email");
                return false;
            }

            const { data, error } = await supabase
                .from("property_invites")
                .insert({
                    property_id: propertyId,
                    inviter_id: user.id,
                    invitee_email: email,
                    status: 'pending'
                })
                .select()
                .single();

            if (error) throw error;

            toast.success(`Invite sent to ${email}`);
            setInvites(prev => [...prev, data]);
            return true;
        } catch (error) {
            console.error("Error sending invite:", error);
            toast.error("Failed to send invite");
            return false;
        }
    };

    const revokeInvite = async (inviteId) => {
        try {
            const { error } = await supabase
                .from("property_invites")
                .delete()
                .eq("id", inviteId);

            if (error) throw error;

            setInvites(prev => prev.filter(i => i.id !== inviteId));
            toast.success("Invite revoked");
            return true;
        } catch (error) {
            console.error("Error revoking invite:", error);
            toast.error("Failed to revoke invite");
            return false;
        }
    };

    const removeMember = async (memberId) => {
        try {
            const { error } = await supabase
                .from("property_members")
                .delete()
                .eq("id", memberId);

            if (error) throw error;

            setMembers(prev => prev.filter(m => m.id !== memberId));
            toast.success("Member removed");
            return true;
        } catch (error) {
            console.error("Error removing member:", error);
            toast.error("Failed to remove member");
            return false;
        }
    };

    // Function to check invites for CURRENT USER (global context usually, but put here for utility)
    const checkMyInvites = useCallback(async () => {
        if (!user || !user.email) return [];

        const { data, error } = await supabase
            .from("property_invites")
            .select(`
                *,
                properties:property_id ( name, suburb )
            `)
            .eq("invitee_email", user.email)
            .eq("status", "pending");

        if (error) {
            console.error("Error checking invites:", error);
            return [];
        }
        return data || [];
    }, [user]);

    const acceptInvite = async (inviteId, _propertyId) => {
        try {
            // Use RPC function to bypass RLS restrictions for inserting members
            const { error } = await supabase
                .rpc('accept_property_invite', { invite_uuid: inviteId });

            if (error) throw error;

            toast.success("Property added to your dashboard!");
            // Refresh local state if successful
            await fetchSharingInfo();
            return true;
        } catch (error) {
            console.error("Error accepting invite:", error);
            toast.error("Failed to accept invite: " + (error.message || "Unknown error"));
            return false;
        }
    };

    const rejectInvite = async (inviteId) => {
        try {
            const { error } = await supabase
                .from("property_invites")
                .update({ status: 'rejected' })
                .eq("id", inviteId);

            if (error) throw error;
            toast.success("Invite declined");
            return true;
        } catch (error) {
            console.error("Error declining invite:", error);
            return false;
        }
    };


    return {
        members,
        invites,
        loading,
        fetchSharingInfo,
        inviteUser,
        revokeInvite,
        removeMember,
        checkMyInvites,
        acceptInvite,
        rejectInvite
    };
};
